// Reason: the "create" method needs implicit return. And it's apparently
// blocked by two eslint commands.
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
// Reason: We don't know external implementation details, hence the usage of "any".
/* eslint-disable @typescript-eslint/no-explicit-any */

import Axios from 'axios';
import { axiosEnhancedErrorAsHttpError } from './_axios_enhanced_error_as_http_error';
import { HttpStatusCode } from './http_status_code';
import { HttpOptions } from './http_options';
import type { HttpMockPayload } from './http_options';
import type { AxiosInstance } from 'axios';
import type { HttpResponse, HttpRequestConfig, HttpCancelTokenSource, Payload } from './common';

const clients: {
	[key: string]: AxiosInstance | null;
} = Object.create(null);

async function send<T>(
	fn: typeof Axios.get | typeof Axios.delete | typeof Axios.post | typeof Axios.put,
	url: string,
	options: HttpOptions,
	body?: null | string | number | Record<string, any>,
): Promise<HttpResponse<T>> {
	let response: HttpResponse<T>;

	if (options.mock != null) {
		response = {
			data: (options.mock as HttpMockPayload<T>).data,
			status: options.mock.status ?? HttpStatusCode.Ok200, // Defaults to Ok so developers doesn't need to explicity type the default.
			config: options.requestConfig as any,
			headers: options.mock.headers ?? {},
			statusText: '',
		};
	} else {
		try {
			if (body === undefined) {
				response = await (fn as typeof Axios.get | typeof Axios.delete | typeof Axios.head | typeof Axios.options)(url, {
					...options.requestConfig,
					cancelToken: options.cancelToken?.token ?? Axios.CancelToken.source().token,
				});
			} else {
				response = await (fn as typeof Axios.post | typeof Axios.put | typeof Axios.patch)(
					url,
					body, // We're not transforming this anymore since it's being done internally in Axios https://github.com/axios/axios/blob/master/lib/defaults/index.js#L52
					{
						...options.requestConfig,
						cancelToken: options.cancelToken?.token ?? Axios.CancelToken.source().token,
					},
				);
			}
		} catch (error: unknown) {
			throw axiosEnhancedErrorAsHttpError(error);
		}
	}

	if (options.throttle > 0) {
		await new Promise((resolve) => setTimeout(resolve, options.throttle));
	}

	return response;
}

/**
 * ## @visiba/http
 *
 * Performs HTTP requests with an asynchronous implementation.
 */
export const Http = Object.freeze({
	/**
	 * When invoked, it'll causes a GET request to execute on
	 * the server. Returning a promise with the server's response.
	 */
	get<T>(url: string, optionConfiguration?: HttpOptions): Promise<HttpResponse<T>> {
		return send<T>(Axios.get, url, optionConfiguration ?? new HttpOptions({}));
	},
	/**
	 * When invoked, it'll causes a DELETE request to execute on
	 * the server. Returning a promise with the server's response.
	 */
	delete<T>(url: string, optionConfiguration?: HttpOptions): Promise<HttpResponse<T>> {
		return send<T>(Axios.delete, url, optionConfiguration ?? new HttpOptions({}));
	},
	/**
	 * When invoked, it'll causes a OPTIONS request to execute on
	 * the server. Returning a promise with the server's response.
	 */
	options<T>(url: string, optionConfiguration?: HttpOptions): Promise<HttpResponse<T>> {
		return send<T>(Axios.options, url, optionConfiguration ?? new HttpOptions({}));
	},
	/**
	 * When invoked, it'll causes a HEAD request to execute on
	 * the server. Returning a promise with the server's response.
	 */
	head<T>(url: string, optionConfiguration?: HttpOptions): Promise<HttpResponse<T>> {
		return send<T>(Axios.head, url, optionConfiguration ?? new HttpOptions({}));
	},
	/**
	 * When invoked, it'll causes a POST request to execute on
	 * the server. Returning a promise with the server's response.
	 */
	post<T>(url: string, body: Payload, optionConfiguration?: HttpOptions): Promise<HttpResponse<T>> {
		if (body instanceof HttpOptions) throw new Error(`Option was passed as the body`);

		return send<T>(Axios.post, url, optionConfiguration ?? new HttpOptions({}), body);
	},
	/**
	 * When invoked, it'll causes a PUT request to execute on
	 * the server. Returning a promise with the server's response.
	 */
	put<T>(url: string, body: Payload, optionConfiguration?: HttpOptions): Promise<HttpResponse<T>> {
		if (body instanceof HttpOptions) throw new Error(`Option was passed as the body`);

		return send<T>(Axios.put, url, optionConfiguration ?? new HttpOptions({}), body);
	},
	/**
	 * When invoked, it'll causes a PATCH request to execute on
	 * the server. Returning a promise with the server's response.
	 */
	patch<T>(url: string, body: Payload, optionConfiguration?: HttpOptions): Promise<HttpResponse<T>> {
		if (body instanceof HttpOptions) throw new Error(`Option was passed as the body`);

		return send<T>(Axios.patch, url, optionConfiguration ?? new HttpOptions({}), body);
	},
	/**
	 * Creates a new Http Client which may be used for a long lasting session.
	 */
	create(alias: string) {
		type Ctor<T> = { new (...args: any[]): T };
		let optionCtor: Ctor<HttpOptions> = HttpOptions;

		return {
			/**
			 * When invoked, it'll causes the configured GET request to execute on
			 * the server. Returning a promise with the server's response.
			 */
			get<T>(url: string, optionConfiguration?: HttpOptions): Promise<HttpResponse<T>> {
				return send<T>(clients[alias]?.get ?? Axios.get, url, optionConfiguration ?? new optionCtor({}));
			},
			/**
			 * When invoked, it'll causes the configured DELETE request to execute on
			 * the server. Returning a promise with the server's response.
			 */
			delete<T>(url: string, optionConfiguration?: HttpOptions): Promise<HttpResponse<T>> {
				return send<T>(clients[alias]?.delete ?? Axios.delete, url, optionConfiguration ?? new optionCtor({}));
			},
			/**
			 * When invoked, it'll causes the configured OPTIONS request to execute on
			 * the server. Returning a promise with the server's response.
			 */
			options<T>(url: string, optionConfiguration?: HttpOptions): Promise<HttpResponse<T>> {
				return send<T>(clients[alias]?.options ?? Axios.options, url, optionConfiguration ?? new optionCtor({}));
			},
			/**
			 * When invoked, it'll causes the configured HEAD request to execute on
			 * the server. Returning a promise with the server's response.
			 */
			head<T>(url: string, optionConfiguration?: HttpOptions): Promise<HttpResponse<T>> {
				return send<T>(clients[alias]?.head ?? Axios.head, url, optionConfiguration ?? new optionCtor({}));
			},
			/**
			 * When invoked, it'll causes the configured POST request to execute on
			 * the server. Returning a promise with the server's response.
			 */
			post<T>(url: string, body: Payload, optionConfiguration?: HttpOptions): Promise<HttpResponse<T>> {
				if (body instanceof HttpOptions) throw new Error(`Option was passed as the body`);

				return send<T>(clients[alias]?.post ?? Axios.post, url, optionConfiguration ?? new optionCtor({}), body);
			},
			/**
			 * When invoked, it'll causes the configured PUT request to execute on
			 * the server. Returning a promise with the server's response.
			 */
			put<T>(url: string, body: Payload, optionConfiguration?: HttpOptions): Promise<HttpResponse<T>> {
				if (body instanceof HttpOptions) throw new Error(`Option was passed as the body`);

				return send<T>(clients[alias]?.put ?? Axios.put, url, optionConfiguration ?? new optionCtor({}), body);
			},
			/**
			 * When invoked, it'll causes the configured PATCH request to execute on
			 * the server. Returning a promise with the server's response.
			 */
			patch<T>(url: string, body: Payload, optionConfiguration?: HttpOptions): Promise<HttpResponse<T>> {
				if (body instanceof HttpOptions) throw new Error(`Option was passed as the body`);

				return send<T>(clients[alias]?.patch ?? Axios.patch, url, optionConfiguration ?? new optionCtor({}), body);
			},
			/**
			 * Updates the Http Client with `HttpRequestConfig`.
			 */
			setConfig(config: HttpRequestConfig): void {
				clients[alias] = Axios.create(config);
			},
			/**
			 * Overrides what class Options should be, useful to add a
			 * standardization for each custom request (same headers for each request etc)
			 */
			setCustomOption(option: Ctor<HttpOptions>): void {
				optionCtor = option;
			},
		} as const;
	},
	/**
	 * If needed, this method creates an CancelToken. May be inserted into an
	 * Http.Option for canceling requests prematurely.
	 */
	createCancelTokenSource(): HttpCancelTokenSource {
		return Axios.CancelToken.source();
	},
	/**
	 * Removes a known Http Client.
	 */
	removeClient(alias: string): void {
		delete clients[alias];
	},
	/**
	 * Retrieves an instantiated Http Client.
	 */
	getClient(alias: string): AxiosInstance | null {
		return clients[alias] ?? null;
	},
} as const);
